import React from 'react';
// import ReactEcharts from 'echarts-for-react';
import styled from 'styled-components';
import ReactEcharts from 'echarts-for-react';
import { Loader } from '@exxonmobil/react-unity';
import { CostValidation } from '../../common/types/TrendsTypes';
import { ErrorBox } from '../../common/components/ErrorBox';
import { Period } from './TrendsFieldSet';
import { getNonProdData, getProdAndNonProdData, getProdData } from '../../common/utils/filterData';

interface Props {
  data: CostValidation[];
  isLoading: boolean;
  error: string;
  onRefresh: () => void;
  timePeriod: Period;
  subscription: String;
}

const getResourceCost = (data: CostValidation[]) => {
  const resourcesToCost = new Map<string, number>();
  for (let i = 0; i < data.length; i += 1) {
    const resource = data[i];
    if (!resourcesToCost.has(resource.resourceName)) {
      resourcesToCost.set(resource.resourceName, 0);
    }
    const cur = resourcesToCost.get(resource.resourceName);
    if (typeof cur === 'number') {
      resourcesToCost.set(resource.resourceName, Number((resource.cost + cur).toFixed(2)));
    }
  }

  return resourcesToCost;
};

const getOthersCost = (topResources: [string, number][], topAmount: number) => {
  const othersCost = topResources
    .slice(topAmount)
    .map((resource) => resource[1])
    .reduce((total, cost) => total + cost, 0);

  return parseFloat(othersCost.toFixed(2));
};

const getTopResourceCost = (resourcesToCost: Map<string, number>, topAmount: number) => {
  const topAllResources = [...resourcesToCost.entries()].sort((a, b) => b[1] - a[1]);
  const topResources = topAllResources.slice(0, topAmount);
  const topResourcesMap = new Map(topResources);
  const othersCost = getOthersCost(topAllResources, topAmount);
  topResourcesMap.set('Others', othersCost);
  return topResourcesMap;
};

const DomainChart = ({ data, isLoading, error, onRefresh, timePeriod, subscription }: Props) => {
  // Create a ref for the ReactEcharts component
  if (error) {
    if (error === '401' || error === '403') {
      return <EmptyDataContainer>Sessions timeout, please close the page and login again.</EmptyDataContainer>;
    }
    return <ErrorBox onRefresh={onRefresh} isRefreshable />;
  }
  if (isLoading) {
    return (
      <EmptyDataContainer style={{ paddingBottom: '2rem' }}>
        <Loader data-testid="domain-cost-pie-chart-loading" className="em-u-margin loader" />
      </EmptyDataContainer>
    );
  }
  if (data.length === 0) {
    return <EmptyDataContainer>No data available</EmptyDataContainer>;
  }

  let allResourcesCost;
  switch (subscription) {
    case 'NonProd':
      allResourcesCost = getResourceCost(getNonProdData(data));
      break;
    case 'Prod':
      allResourcesCost = getResourceCost(getProdData(data));
      break;
    case 'All':
    default:
      allResourcesCost = getResourceCost(getProdAndNonProdData(data));
  }
  const topResourcesCost = getTopResourceCost(allResourcesCost, 7);
  const topResourcesNames = [...topResourcesCost.keys()];
  // To be able to display it according to Apache ECharts Pie chart
  const topResourceObjects = Array.from(topResourcesCost, ([name, value]) => ({
    name,
    value,
    itemStyle: name === 'Others' ? { color: '#9c9c9c' } : undefined
  }));
  const totalCost = [...topResourcesCost.values()].reduce((total, cost) => total + cost, 0).toFixed(2);

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      }
    },
    legend: [
      {
        type: 'plain',
        orient: 'vertical',
        right: 50,
        top: 'center',
        pageTextStyle: {
          fontStyle: 'normal',
          fontFamily: 'EMprintRegular'
        },
        data: topResourcesNames
      }
    ],
    toolbox: {
      feature: {
        saveAsImage: {}
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    graphic: [
      {
        type: 'text',
        left: '27%',
        top: 'center',
        style: {
          text: `${timePeriod}\nTotal Cost:\n$${totalCost}`,
          fill: '#333',
          font: '15px EMprintRegular',
          overflow: 'break',
          textAlign: 'center'
        }
      }
    ],
    series: [
      {
        name: 'Cost',
        type: 'pie',
        center: ['33%', '50%'],
        radius: ['50%', '90%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: 'inner',
          formatter: `{d}%`
        },
        data: topResourceObjects.sort(function compare(a, b) {
          return a.value - b.value;
        })
      }
    ]
  };

  return (
    <ReactEcharts opts={{ renderer: 'svg', width: 'auto', height: 300 }} option={option} style={{ height: '300px' }} />
  );
};

const EmptyDataContainer = styled.div`
  text-align: center;
  padding-top: 2rem;
  min-height: 5rem;
`;

export default DomainChart;
